// What `events.is_all_day` is allowed to mean when we WRITE a row. Issue #607.
//
// ── The distinction ────────────────────────────────────────────────────────
//
// "All day" and "no time yet" are opposites, and the events table had been
// collapsing them into one flag:
//
//   all-day  → she told us exactly when: the whole day. Nothing to ask.
//   TBD      → we do not know when. This is the one case that NEEDS asking.
//
// Every write site computed `is_all_day` as "there is no start time", so a TBD
// event was stored as all-day and read back as all-day. The information that we
// never knew the time was destroyed on the first save, and no column still held
// it. Two things followed:
//
//   1. `needsTimeAnswer()` returns false for all-day items by design, so the
//      "What time?" Quick Question — the only mechanism that exists to recover a
//      missing time — was suppressed for exactly the events that needed it.
//   2. The card rendered an ALL DAY badge, which is a confident claim about her
//      day that Kin never had grounds to make.
//
// ── Why this is a shared module and not another inline expression ──────────
//
// The decision was written out by hand at five separate write sites, and all
// five got it wrong the same way. A sixth (TodayTab's edit-event update payload,
// ~L11052) got it RIGHT — which is the tell: this is a rule, not a line, and
// three hand-synced copies of one rule is this codebase's most common defect
// shape (#361, #470, #515).
//
// ⚠️ Read-side helpers `isAllDayTime` / `isTbdTime` in TodayTab.tsx are correct
// and deliberately untouched (#607 says so explicitly). This module is the
// WRITE-side counterpart and mirrors `isAllDayTime`'s normalisation exactly —
// trim, lowercase, strip spaces/underscores/hyphens, compare to "allday" — so
// the two can never disagree about what an all-day marker looks like.

/**
 * True only for a genuine all-day marker. Mirrors TodayTab's `isAllDayTime`.
 *
 * Accepts the shapes the app actually produces: "all-day" (the parser's
 * canonical form), "ALL-DAY", "all day", "All_Day".
 */
export function isAllDayMarker(time?: string | null): boolean {
  if (!time) return false;
  return String(time).trim().toLowerCase().replace(/[\s_-]/g, "") === "allday";
}

/**
 * The `is_all_day` / `start_time` pair to write for a given display time.
 *
 * `time24` is the parsed 24-hour clock value ("14:30") when one was found, or
 * null. It is deliberately NOT used to infer all-day: its absence means "no
 * clock time", which covers BOTH all-day and TBD, and conflating those is the
 * entire bug.
 */
export function eventTimeColumns(
  displayTime?: string | null,
  time24?: string | null
): { is_all_day: boolean; start_time: string | null } {
  if (isAllDayMarker(displayTime)) {
    return { is_all_day: true, start_time: null };
  }
  return {
    is_all_day: false,
    start_time: time24 ? (time24.length === 5 ? `${time24}:00` : time24) : null,
  };
}
